import { jsxs, jsx } from 'react/jsx-runtime';
import { useRef, useState, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import useResizeObserver from 'use-resize-observer';

const TruncatedText = ({ children, className })=>{
    /**
     * Ref for determining text height based on font-size (invisible copy).
     */ const fontRef = useRef(null);
    /**
     * Ref for determining if text overflows (i.e., needs to be truncated).
     */ const overflowRef = useRef(null);
    /**
     * Fixed height for containers (to force overflow).
     */ const [fixedHeight, setFixedHeight] = useState(0);
    /**
     * Boolean to determine if we should truncate text w/ ellipsis.
     */ const [truncateText, setTruncateText] = useState(false);
    /**
     * Calculate the middle point where to split text.
     */ const middle = Math.floor(children.length / 2);
    /**
     * Ref and "resize" callback for "truncated text" wrapper <div>.
     */ const { ref: wrapper } = useResizeObserver({
        onResize: ()=>{
            if (overflowRef.current) {
                /**
                 * If a text has overflow, we should truncate.
                 */ setTruncateText((overflowRef.current?.scrollHeight ?? 0) > fixedHeight);
            }
        }
    });
    /**
     * Determine fixed height using fontRef <div> (will vary depending on font-size).
     */ useEffect(()=>{
        if (fontRef.current) {
            setFixedHeight(fontRef.current?.clientHeight);
        }
    }, [
        fixedHeight
    ]);
    /**
     * Determine "start" and "end" text.
     */ const text = useMemo(()=>{
        const textWSpaces = children.replace(/ /g, '\u00a0'); // replace w/ unicode spaces
        return {
            start: textWSpaces.substring(0, middle),
            end: textWSpaces.substring(middle).split('').reverse().join('')
        };
    }, [
        children,
        middle
    ]);
    return jsxs("div", {
        ref: wrapper,
        className: classNames('truncated-text', className),
        style: {
            height: fixedHeight
        },
        children: [
            jsx("div", {
                ref: fontRef,
                "aria-hidden": "true",
                className: "absolute w-0 overflow-hidden",
                "data-testid": "font-reference",
                children: "."
            }),
            jsx("div", {
                ref: overflowRef,
                "aria-hidden": "true",
                className: classNames('untruncated', {
                    visible: !truncateText,
                    'invisible pointer-events-none': truncateText
                }),
                "data-testid": "untruncated-text",
                style: {
                    height: fixedHeight,
                    marginTop: truncateText && fixedHeight > 0 ? -fixedHeight : undefined
                },
                children: children
            }),
            jsxs("div", {
                "aria-hidden": "true",
                className: classNames('truncated', {
                    visible: truncateText,
                    'invisible pointer-events-none': !truncateText
                }),
                "data-testid": "truncated-text",
                children: [
                    jsx("div", {
                        "aria-hidden": "true",
                        className: "start",
                        "data-testid": "start",
                        style: {
                            height: fixedHeight
                        },
                        children: text.start
                    }),
                    jsx("div", {
                        children: "..."
                    }),
                    jsx("div", {
                        className: "end",
                        "data-testid": "end",
                        dir: "rtl",
                        style: {
                            height: fixedHeight
                        },
                        children: text.end
                    })
                ]
            }),
            jsx("div", {
                className: "sr-only",
                children: children
            })
        ]
    });
};
TruncatedText.displayName = 'TruncatedText';

export { TruncatedText, TruncatedText as default };
//# sourceMappingURL=TruncatedText.es.js.map
